import { AppFeatures, AppDescription, getAppIconImage } from "common/components/SerializedApp";
import { AppUserAuthenticator, DataStore, MinimalAppEnvironment } from "common/components/types";
import { FirestoreDataStore } from "common/components/datastore/firestore/firestore-datastore";
import { ForwardingAppEnvironment } from "./forwarding-app-environment";
import { getBuilderAppDatabase, getPlayerDatabase, waitForFirestoreLibraries } from "./web-database";
import { TypeSchema } from "common/generator/description";
import { getStaticAppConfig } from "common/static-app";
import { dontAwait } from "common/Support";
import { getUserProfileTableInfo } from "common/components/user-profile";

export function makeAppEnvironmentForAppDescription(
    appDescription: AppDescription,
    schema: TypeSchema | undefined,
    authenticator: AppUserAuthenticator,
    appFeatures: AppFeatures,
    isBuilder: boolean
): ForwardingAppEnvironment {
    const { appID } = authenticator;
    const db = isBuilder ? getBuilderAppDatabase(appID) : getPlayerDatabase(appID);
    const dataStore = new FirestoreDataStore(db, schema, authenticator, isBuilder, isBuilder, undefined);

    const staticAppConfig = getStaticAppConfig();
    if (staticAppConfig !== undefined) {
        const { publishedAppData: appData } = staticAppConfig;
        // Too many code paths assume we've loaded the Firestore libraries.
        // We have to wait for them before setting the schema.
        dontAwait(
            waitForFirestoreLibraries().then(() => {
                dataStore.setSchema(appData.schema);
            })
        );
    }

    const appEnvironment = new ForwardingAppEnvironment(
        authenticator,
        appFeatures,
        db,
        dataStore,
        isBuilder,
        getUserProfileTableInfo(appDescription),
        getAppIconImage(appDescription)
    );
    dataStore.setAppEnvironment(appEnvironment);
    return appEnvironment;
}

export function makeMinimalAppEnvironmentAndDataStore(
    appDescription: AppDescription,
    schema: TypeSchema | undefined,
    authenticator: AppUserAuthenticator
): [MinimalAppEnvironment, DataStore] {
    const { appID } = authenticator;
    const db = getPlayerDatabase(appID);
    const dataStore = new FirestoreDataStore(db, schema, authenticator, false, true, undefined);

    const staticAppConfig = getStaticAppConfig();
    if (staticAppConfig !== undefined) {
        const { publishedAppData: appData } = staticAppConfig;
        // Too many code paths assume we've loaded the Firestore libraries.
        // We have to wait for them before setting the schema.
        dontAwait(
            waitForFirestoreLibraries().then(() => {
                dataStore.setSchema(appData.schema);
            })
        );
    }

    const env: MinimalAppEnvironment = {
        authenticator,
        locationSettings: undefined,
        database: db,
        isGeocodeWithinQuota: () => true,
        userProfileTableInfo: getUserProfileTableInfo(appDescription),
    };
    dataStore.setAppEnvironment(env);
    return [env, dataStore];
}
